import { SendsPostageDetailType, useSendsPageDetail } from '@ukpostbox/client-data';
import { SendsAddressDetailType, SendsPageDetailType } from '@ukpostbox/client-data/lib/Types/StandaloneSendsTypes';
import { SelectItemType } from '@ukpostbox/frontend-framework';
import { GetArrayIndexByKeyValue } from '@ukpostbox/helpers';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

interface SendMailProviderType {
	address: SendsAddressDetailType | null;
	postage: SendsPostageDetailType | null;
	sendPageID: number;
	file: any;
	reset: boolean;

	addressOptions: Array<SelectItemType>;
	postageOptions: Array<SelectItemType>;

	UpdatePageId: (v: number) => void;
	UpdateAddress: (v: number, newAddress?: boolean) => void;
	UpdatePostage: (v: number) => void;
	UpdateFile: (v: any) => void;
	ResetSendMail: () => void;

	sendPageLoading: boolean;
	sendPageData: SendsPageDetailType | undefined;
	sendPageError: any;
}

const SendMailContext = React.createContext<SendMailProviderType>({} as SendMailProviderType);

function SendMailProvider(props: any) {
	const { pageId } = useParams();

	const [sendPageID, setSendPageID] = useState<number>(parseInt(pageId as string, 10));

	const [address, setAddress] = useState<SendsAddressDetailType | null>(null);
	const [postage, setPostage] = useState<SendsPostageDetailType | null>(null);
	const [file, setFile] = useState<any>(null);

	const [reset, setReset] = useState<boolean>(false);

	const { isLoading: sendPageLoading, data: sendPageData, error: sendPageError } = useSendsPageDetail(sendPageID);

	const [addressOptions, setAddressOptions] = useState<Array<SelectItemType>>([]);
	const [postageOptions, setPostageOptions] = useState<Array<SelectItemType>>([]);

	const ResetPostage = () => {
		setPostageOptions([]);
		setPostage(null);
	};

	const ResetSendMail = () => {
		ResetPostage();
		setAddress(null);
		setFile(null);
		setReset(!reset);
	};

	const UpdatePageId = (value: number) => {
		setSendPageID(value);
	};

	const UpdatePostage = (postageId: number) => {
		if (sendPageData) {
			const addressIndex = GetArrayIndexByKeyValue(sendPageData.addresses, 'value', address?.value);
			const newActive = sendPageData.addresses[addressIndex].postageOptions.filter(
				(thisPostage) => thisPostage.id === postageId,
			)[0];
			setPostage(newActive);
		}
	};

	const UpdateFile = (value: any) => {
		setFile(value);
	};

	const MapAddressData = () => {
		const filteredAddress: Array<any> = [];

		if (sendPageData?.addresses) {
			sendPageData.addresses.forEach((thisAddress) => {
				filteredAddress.push({ label: thisAddress.value, value: thisAddress.id });
			});
		}

		if (filteredAddress.length === 1) {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			UpdateAddress(filteredAddress[0].value);
		}

		setAddressOptions(filteredAddress);
	};

	const UpdateAddress = (addressId: number, newAddress: boolean = false) => {
		if (sendPageData) {
			const newActive = sendPageData?.addresses.filter((thisAddress) => thisAddress.id === addressId)[0];

			if (newAddress) {
				MapAddressData();
			}

			setAddress(newActive);
		}
	};

	const MapPostageData = () => {
		const filteredPostage: Array<any> = [];

		if (address && sendPageData?.addresses) {
			const addressIndex = GetArrayIndexByKeyValue(sendPageData.addresses, 'value', address.value);

			sendPageData.addresses[addressIndex].postageOptions.forEach((thisAddress) => {
				filteredPostage.push({ label: thisAddress.value, value: thisAddress.id });
			});

			if (filteredPostage.length === 1) {
				UpdatePostage(filteredPostage[0].value);
			}

			setPostageOptions(filteredPostage);
		}
	};

	useEffect(() => {
		ResetSendMail();
	}, [sendPageID]);

	useEffect(() => {
		if (!sendPageLoading) {
			MapAddressData();
		}
	}, [reset, sendPageLoading]);

	useEffect(() => {
		if (!sendPageLoading && address) {
			MapPostageData();
		}
	}, [address, sendPageLoading]);

	const sendMailMemo = useMemo(
		() => ({
			address,
			postage,
			sendPageID,
			file,
			reset,

			addressOptions,
			postageOptions,

			sendPageLoading,
			sendPageData,
			sendPageError,

			UpdatePageId,
			UpdateAddress,
			UpdatePostage,
			UpdateFile,
			ResetSendMail,
		}),
		[
			address,
			postage,
			sendPageID,
			file,
			reset,

			addressOptions,
			postageOptions,

			sendPageLoading,
			sendPageData,
			sendPageError,
		],
	);

	return <SendMailContext.Provider value={sendMailMemo}>{props.children}</SendMailContext.Provider>;
}

const useSendMail = () => useContext(SendMailContext);

export { SendMailContext, useSendMail };

export default SendMailProvider;
